import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  useTheme,
} from "@mui/material";
import AustraliaSvg from "icons/australia.svg?react";
import GeoscapeDeveloperSvg from "icons/hub-logo-signIn.svg?react";
import {
  BadgeCheck,
  Clock,
  CodeXml,
  CreditCard,
  Grid2x2Check,
  Layers,
  Map,
  MapPinned,
} from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { getRedirectInfo } from "utils/redirect-manager";

interface Point {
  title: string;
  subtitle?: string;
  icon: ReactElement;
}

interface MarketingContent {
  heading: string;
  subheading?: string;
  points: Point[];
}

export const MarketingPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Content map based on utm_campaign values
  const campaignContentMap: Record<string, MarketingContent> = {
    "env-modelling": {
      heading:
        "Create a free Geoscape Hub account to select a location and unlock insights.",
      subheading:
        "No credit card is needed - sign up today and start exploring the data.",
      points: [
        {
          title: "National Coverage",
          subtitle:
            "Authoritative, up-to-date data on buildings, land parcels and planning.",
          icon: <AustraliaSvg stroke={isDark ? "#90CAF9" : "black"} />,
        },
        {
          title: "Save Time",
          subtitle:
            "Clip what you need instantly instead of digitising or chasing open data.",
          icon: <Clock size="32px" />,
        },
        {
          title: "Self Service",
          subtitle:
            "Define sites, assign costs to clients or cost centres, and control spend.",
          icon: <Grid2x2Check size="32px" />,
        },
      ],
    },
    "addr-verify": {
      heading:
        "Create a free Geoscape Hub account to select a location and unlock insights.",
      subheading:
        "No credit card is needed - sign up today and start exploring the data.",
      points: [
        {
          title: "Address Verification",
          subtitle:
            "The only verification service powered by G-NAF Live, updated daily.",
          icon: <MapPinned size="32px" />,
        },
        {
          title: "Verify and Locate",
          subtitle:
            "Real-time verification and geocoding ensure every address is correct and accurately positioned.",
          icon: <BadgeCheck size="32px" />,
        },
        {
          title: "Enrich Customer Data",
          subtitle:
            "Enhance decision-making with property intelligence through our APIs.",
          icon: <Layers size="32px" />,
        },
      ],
    },
    default: {
      heading: "Start using Hub for free. No credit card required!",
      points: [
        {
          title:
            "Visualise the entire Geoscape product catalogue in a fast and responsive map interface.",
          icon: <Map size="32px" />,
        },
        {
          title: "Explore our range of address and building APIs.",
          icon: <CodeXml size="32px" />,
        },
        {
          title: "Download our datasets of your area of interest.",
          icon: <MapPinned size="32px" />,
        },
        {
          title:
            "Your gateway to empowered, comprehensive location enablement.",
          icon: <CreditCard size="32px" />,
        },
      ],
    },
  };

  const [content, setContent] = useState<MarketingContent>(
    campaignContentMap.default
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const utmCampaign = searchParams.get("utm_campaign");
    if (utmCampaign && campaignContentMap[utmCampaign]) {
      setContent(campaignContentMap[utmCampaign]);
    } else if (!utmCampaign) {
      // Check pendingRedirect local storage for utm_campaign
      const redirectInfo = getRedirectInfo();
      if (redirectInfo?.query) {
        const pendingParams = new URLSearchParams(redirectInfo.query);
        const pendingUtmCampaign = pendingParams.get("utm_campaign");
        if (pendingUtmCampaign && campaignContentMap[pendingUtmCampaign]) {
          setContent(campaignContentMap[pendingUtmCampaign]);
          return;
        }
      }
      setContent(campaignContentMap.default);
    } else {
      setContent(campaignContentMap.default);
    }
  }, [location.search]);

  return (
    <Box
      sx={{
        minWidth: "340px",
        maxWidth: "500px",
        my: 2,
        ml: 2,
        border: "none",
        backgroundImage: "none",
        gap: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo */}
      <Box display="flex" justifyContent="start">
        <GeoscapeDeveloperSvg
          fill={isDark ? "white" : "black"}
          style={{ width: "228px", height: "52px" }}
        />
      </Box>

      {/* Heading */}
      <Typography variant="h6" fontWeight="bold">
        {content.heading}
      </Typography>

      {/* Subheading */}
      {content.subheading && (
        <Typography variant="body1">{content.subheading}</Typography>
      )}
      {/* Main Points */}
      <List disablePadding>
        {content.points.map((point, index) => (
          <ListItem
            key={`point-${index}`}
            sx={{
              paddingX: 2,
              paddingY: 1,
              alignItems: "center",
            }}
          >
            <ListItemIcon sx={{ minWidth: "40px", marginTop: "4px" }}>
              <Box
                sx={{
                  color: isDark ? "#90CAF9" : "black",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {point.icon}
              </Box>
            </ListItemIcon>
            <Box>
              <Typography
                color="text.secondary"
                variant={point.subtitle ? "body1" : "body2"}
                sx={{
                  fontWeight: point.subtitle ? "bold" : 500,
                  marginBottom: point.subtitle ? "4px" : 0,
                }}
              >
                {point.title}
              </Typography>
              {point.subtitle && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{
                    color: isDark ? "#999" : "#666",
                    lineHeight: 1.5,
                  }}
                >
                  {point.subtitle}
                </Typography>
              )}
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default MarketingPage;
